import { useEffect } from 'react';
import { ThumbsDown, X } from 'lucide-react';

export interface VotedOutSong {
  id: string;
  title: string;
  artist: string;
  sharePercent: number;
}

interface VotedOutModalProps {
  song: VotedOutSong;
  returnPercent: number;
  saving?: boolean;
  onHelp: () => void;
  onDontCare: () => void;
  onClose: () => void;
}

// First notice for one voted-out song (ITER-144 / ITER-190): help bring it back, or don't hear about it again
export function VotedOutModal({ song, returnPercent, saving = false, onHelp, onDontCare, onClose }: VotedOutModalProps) {
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="voted-out-modal">
      <div
        className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-sm w-full max-h-[90vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="A song was voted out"
      >
        <div className="flex justify-end p-2">
          <button onClick={onClose} className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300" aria-label="Close">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="px-6 pb-6 space-y-4">
          <ThumbsDown className="w-10 h-10 mx-auto text-red-500" />
          <p className="text-sm text-gray-700 dark:text-gray-300">
            <strong className="text-gray-900 dark:text-white"><em>{song.title}</em> was voted out.</strong>
            {' '}{Math.round(song.sharePercent)}% of everyone taking part are against it right now. It comes back under {returnPercent}%.
          </p>
          <div className="space-y-2">
            <button type="button" onClick={onHelp} disabled={saving} className="btn btn-primary w-full">
              Help bring it back
            </button>
            <button type="button" onClick={onDontCare} disabled={saving} className="btn btn-secondary w-full">
              Don&apos;t care — don&apos;t tell me about this song again
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
